<template>
    <div class="tariff-limits bg-white border-round-lg px-3 py-4 md:p-5 my-3">
        <div class="flex flex-column gap-2 text-center mb-4 md:mb-5">
            <h1 class="tariff-limits__title m-0">{{ $t('lang.pages.tariff_limits.title') }}</h1>
            <span class="text-sm text-color-secondary">{{ $t('lang.pages.tariff_limits.description') }}</span>
        </div>
        <div class="flex flex-column lg:flex-row gap-4">
            <div class="flex flex-column gap-5 flex-1 min-w-0">
                <tariff-limits-input v-model="limits" />
                <div class="flex flex-column gap-3">
                    <label class="flex align-items-center gap-2 cursor-pointer w-max">
                        <InputSwitch v-model="isAddTariff" />
                        <span class="text-base font-bold">{{ $t('lang.pages.tariff_limits.add_tariff') }}</span>
                    </label>
                    <template v-if="isAddTariff">
                        <ul class="tariff-limits__terms">
                            <li
                                v-for="(percent, months) in tariffData.percents"
                                :key="months"
                                :class="{active: Number(months) === activeMonths}"
                                @click="activeMonths = Number(months)"
                            >
                                {{ $t('lang.main.month_' + months) }}
                                <span v-if="percent > 0" class="tariff-limits__percent">-{{ percent }}%</span>
                            </li>
                        </ul>
                        <div class="tariff-limits__cards" :style="{'--cards-count': Math.max(availableTemplates.length, 2)}">
                            <label
                                v-for="template in availableTemplates"
                                :key="template.id"
                                class="tariff-limits__card"
                                :class="{active: template.id === selectedTariffId, disabled: isTemplateBlocked(template)}"
                                v-tooltip.top="isTemplateBlocked(template) ? $t('lang.pages.tariff_limits.blocked_tariff') : null"
                            >
                                <RadioButton v-model="selectedTariffId" :value="template.id" :disabled="isTemplateBlocked(template)" />
                                <span class="text-sm font-medium mt-2">{{ template.label }}</span>
                                <span v-if="activePercent > 0" class="text-sm text-color-secondary line-through">
                                    {{ formatPrice(template.price) }}
                                </span>
                                <span>
                                    <span class="text-xl font-bold white-space-nowrap">{{ formatPrice(getMonthPrice(template)) }}</span>
                                    <span class="text-xs text-color-secondary ml-1">{{ $t('lang.pages.tariff_limits.per_month') }}</span>
                                </span>
                                <span class="text-xs text-color-secondary">
                                    {{ $t('lang.pages.tariff_limits.tariff_limits', [formatNumber(template.aiExecutePerMonth)]) }}
                                </span>
                            </label>
                        </div>
                    </template>
                    <adapters-message v-else-if="!currentTariffId" severity="warn">
                        <div class="flex gap-2 text-sm">
                            <i class="pi pi-exclamation-triangle mt-1"></i>
                            <span>{{ $t('lang.pages.tariff_limits.no_tariff_warning') }}</span>
                        </div>
                    </adapters-message>
                </div>
            </div>
            <div class="tariff-limits__aside flex flex-column gap-3">
                <div class="tariff-limits__summary flex flex-column gap-4">
                    <div class="flex flex-column">
                        <template v-if="isAddTariff">
                            <div class="tariff-limits__summary_row">
                                <span class="text-color-secondary">{{ $t('lang.pages.tariff_limits.summary.tariff') }}</span>
                                <span class="font-bold text-right">{{ selectedTemplate.label }}</span>
                            </div>
                            <div class="tariff-limits__summary_row">
                                <span class="text-color-secondary">{{ $t('lang.pages.tariff_limits.summary.srok') }}</span>
                                <span class="font-bold">{{ $t('lang.main.month_' + activeMonths) }}</span>
                            </div>
                        </template>
                        <div class="tariff-limits__summary_row">
                            <span class="text-color-secondary">{{ $t('lang.pages.tariff_limits.summary.limits') }}</span>
                            <span class="font-bold">{{ formatNumber(limits) }}</span>
                        </div>
                        <div class="tariff-limits__summary_row border-none">
                            <span class="flex align-items-center gap-2 text-color-secondary line-height-1">
                                {{ $t('lang.pages.tariff_limits.summary.cost') }}
                                <Popper hover arrow>
                                    <i class="pi pi-info-circle cursor-pointer"></i>
                                    <template #content>
                                        <div class="text-xs text-left flex flex-column gap-1">
                                            <div v-if="isAddTariff">
                                                <span v-html="$t('lang.pages.tariff_limits.summary.cost_tooltip_tariff', [formatPrice(tariffCost)])"></span>
                                            </div>
                                            <div v-if="conversionDiscount > 0">
                                                <span v-html="$t('lang.pages.tariff_limits.summary.cost_tooltip_conversion', [formatPrice(conversionDiscount)])"></span>
                                            </div>
                                            <div>
                                                <span v-html="$t('lang.pages.tariff_limits.summary.cost_tooltip_limits', [formatPrice(limits)])"></span>
                                            </div>
                                        </div>
                                    </template>
                                </Popper>
                            </span>
                            <span class="text-2xl font-bold white-space-nowrap">{{ formatPrice(totalCost) }}</span>
                        </div>
                        <div v-if="isAddTariff && tariffSaving > 0" class="flex justify-content-end">
                            <span class="tariff-limits__saving">
                                {{ $t('lang.pages.tariff_limits.summary.saving', [formatPrice(tariffSaving)]) }}
                            </span>
                        </div>
                    </div>
                    <Button
                        :label="$t('lang.pages.tariff_limits.create_bill')"
                        class="w-full"
                        size="large"
                        icon="pi pi-arrow-up-right"
                        iconPos="right"
                        @click="onCreateBill"
                    />
                    <form ref="billForm" :action="'/' + locale + '/tarif/limits'" method="post" class="hidden">
                        <input type="hidden" name="act" value="createBill" />
                        <input type="hidden" name="limits" :value="limits" />
                        <template v-if="isAddTariff">
                            <input type="hidden" name="tarif" :value="selectedTariffId" />
                            <input type="hidden" name="srok" :value="activeSrok" />
                        </template>
                    </form>
                </div>
                <div class="tariff-limits__support text-sm text-center text-color-secondary">
                    {{ $t('lang.pages.tariff_limits.support') }}
                    <a href="#" class="text-primary" @click.prevent="onOpenVerbox">{{ $t('lang.pages.tariff_limits.support_link') }}</a>.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Button from "primevue/button";
import InputSwitch from "primevue/inputswitch";
import RadioButton from "primevue/radiobutton";
import {FormatHelper} from "keysso/helpers/FormatHelper";
import Verbox from "keysso/mixins/Verbox";
import AdaptersMessage from "../../component/adapters/Message.vue";
import TariffLimitsInput from "./component/LimitsInput.vue";

export default {
    name: "TariffLimitsPageComponent",
    mixins: [Verbox],
    props: {
        locale: {
            type: String,
            required: true,
        },
        tariffData: {
            percents: Object,
            templates: Object,
            termRelations: Object,
        },
        // 0 - пользователь без активного тарифа
        currentTariffId: {
            type: Number,
            required: true,
        },
        // текущий или последний тариф пользователя, иначе Базовый
        defaultTariffId: {
            type: Number,
            required: true,
        },
        // текущий или последний срок пользователя, иначе 3 месяца
        defaultMonths: {
            type: Number,
            required: true,
        },
        // остаток текущего тарифа, зачитывается при переходе на тариф выше (UserService::getBalanceInDays)
        balanceInDays: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            limits: 25000,
            // по умолчанию тариф добавляется только пользователю без активного тарифа
            isAddTariff: !this.currentTariffId,
            activeMonths: this.defaultMonths,
            selectedTariffId: this.defaultTariffId,
        }
    },
    computed: {
        // доступны текущий тариф пользователя и выше
        availableTemplates() {
            return Object.values(this.tariffData.templates)
                .filter(template => template.id >= this.currentTariffId);
        },
        selectedTemplate() {
            return this.tariffData.templates[this.selectedTariffId];
        },
        activePercent() {
            return this.tariffData.percents[this.activeMonths];
        },
        activeSrok() {
            return Object.keys(this.tariffData.termRelations)
                .find(srok => this.tariffData.termRelations[srok] === this.activeMonths);
        },
        tariffCost() {
            return this.getTariffCost(this.selectedTemplate);
        },
        conversionDiscount() {
            return this.isAddTariff && this.isTariffChange(this.selectedTemplate) ? this.balanceInDays : 0;
        },
        tariffSaving() {
            return this.selectedTemplate.price * this.activeMonths - this.tariffCost;
        },
        // 1 лимит = 1 ₽
        // тариф со скидкой за остаток не дешевле 100 ₽, как в TariffTotalPrice::getTotalPrice
        totalCost() {
            return this.limits + (this.isAddTariff ? Math.max(this.tariffCost - this.conversionDiscount, 100) : 0);
        },
    },
    watch: {
        // при смене срока выбранный тариф может стать недоступным — возвращаем текущий
        activeMonths() {
            if (this.isTemplateBlocked(this.selectedTemplate)) {
                this.selectedTariffId = this.currentTariffId;
            }
        },
    },
    methods: {
        formatNumber(number) {
            return FormatHelper.number(number);
        },
        formatPrice(number) {
            return FormatHelper.number(number, '₽');
        },
        getMonthPrice(template) {
            return Math.round(template.price * (1 - this.activePercent / 100));
        },
        // расчёт аналогичен TariffService::getRealPrice
        getTariffCost(template) {
            return Math.round(template.price * this.activeMonths * (1 - this.activePercent / 100));
        },
        // смена на тариф выше текущего, с конвертацией остатка
        isTariffChange(template) {
            return this.currentTariffId > 0 && template.id > this.currentTariffId;
        },
        // остаток нельзя конвертировать в тариф дешевле него
        isTemplateBlocked(template) {
            return this.isTariffChange(template) && this.balanceInDays > this.getTariffCost(template);
        },
        onCreateBill() {
            this.$refs.billForm.submit();
        },
    },
    components: {
        AdaptersMessage,
        Button,
        InputSwitch,
        RadioButton,
        TariffLimitsInput,
    },
}
</script>

<style scoped>
.tariff-limits {
    max-width: 920px;
    margin: 0 auto;
    color: #232D35;
}
.tariff-limits__title {
    font-size: 28px;
    line-height: 1.3;
    font-weight: 700;
}
.tariff-limits__terms {
    list-style: none;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
    margin: 16px 0 0;
    padding: 4px;
    border: 1px solid #CED4DA;
    border-radius: 8px;
}
.tariff-limits__terms li {
    position: relative;
    padding: 8px;
    border-radius: 4px;
    text-align: center;
    font-size: 16px;
    line-height: 24px;
    white-space: nowrap;
    color: #6C757D;
    cursor: pointer;
}
.tariff-limits__terms li.active {
    background: #EBF1F7;
    color: #232D35;
    cursor: default;
}
.tariff-limits__percent {
    position: absolute;
    top: -18px;
    left: 50%;
    transform: translateX(-50%);
    background: #FFE500;
    color: #232D35;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 12px;
    line-height: 18px;
    font-weight: 700;
}
.tariff-limits__cards {
    display: grid;
    grid-template-columns: repeat(var(--cards-count), minmax(0, 1fr));
    gap: 16px;
}
.tariff-limits__card {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px;
    border: 1px solid #CED4DA;
    border-radius: 8px;
    cursor: pointer;
}
.tariff-limits__card.active {
    border: 2px solid #6EB116;
    padding: 11px;
    background: #EBF1F7;
}
.tariff-limits__card.disabled {
    opacity: .5;
    cursor: not-allowed;
}
.tariff-limits__aside {
    width: 100%;
}
.tariff-limits__summary,
.tariff-limits__support {
    background: #F0F4F7;
    border-radius: 16px;
    padding: 16px 20px;
}
.tariff-limits__summary_row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 12px 0;
    border-bottom: 1px solid #CED4DA;
}
.tariff-limits__saving {
    background: #FFE500;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 700;
}

@media (min-width: 992px) {
    .tariff-limits__aside {
        width: 300px;
        flex-shrink: 0;
    }
}
@media (max-width: 576px) {
    .tariff-limits__title {
        font-size: 22px;
    }
    .tariff-limits__terms {
        grid-template-columns: repeat(2, 1fr);
        row-gap: 24px;
    }
    .tariff-limits__terms li {
        font-size: 14px;
    }
    .tariff-limits__cards {
        grid-template-columns: minmax(0, 1fr);
        gap: 8px;
    }
}
</style>
